import { useEffect, useId, useRef } from 'react'

import { useClipboardCopy } from '@/lib/use-clipboard-copy'
import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { DataTable, Th } from '@/ui/data-table'
import { inputClassName } from '@/ui/input'
import {
  resolveEventRulePolicy, resolvePreferredCounterPolicy,
  type EventRuleChoices, type EventRuleComparison,
  type PolicyFieldChoice, type PolicyFieldComparison,
  type PreferredCounterChoices, type PreferredCounterComparison,
} from './printer-policy-comparison'

type ReviewProps = {
  draftText: string
  repeated: boolean
  disabled: boolean
  resolveDisabled?: boolean
  saveDisabled?: boolean
  onContinue: () => void
  onEditMerged: () => void
  onSaveResolved: () => void
} & ({
  kind: 'counter'
  formatsReadOnly?: boolean
  comparison: PreferredCounterComparison
  choices: PreferredCounterChoices
  onChoose: (field: keyof PreferredCounterChoices, choice: PolicyFieldChoice) => void
} | {
  kind: 'event'
  comparison: EventRuleComparison
  choices: EventRuleChoices
  onChoose: (field: keyof EventRuleChoices, choice: PolicyFieldChoice) => void
})

export function PrinterPolicyConflictReview(props: ReviewProps) {
  const heading = useRef<HTMLHeadingElement>(null)
  const headingId = useId()
  useEffect(() => { heading.current?.focus() }, [])
  let complete: boolean
  if (props.kind === 'counter') {
    const choices: PreferredCounterChoices = props.formatsReadOnly ? { ...props.choices, formatPriority: 'latest' } : props.choices
    complete = resolvePreferredCounterPolicy(props.comparison, choices) !== null
  } else complete = resolveEventRulePolicy(props.comparison, props.choices) !== null
  const blocked = props.disabled || props.resolveDisabled || !complete
  return (
    <section data-slot="printer-policy-conflict-review" className="flex min-w-0 flex-col gap-4" aria-labelledby={headingId}>
      <h2 ref={heading} id={headingId} tabIndex={-1} className="text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring">Review and resolve changes</h2>
      <p className="text-muted-foreground text-xs">This policy changed while you were editing. Your changes are preserved. Review the latest version before saving.</p>
      <DataTable caption="Policy changes" empty={false} emptyMessage="" headers={[
        <Th key="field">Field</Th>,
        <Th key="opened">{props.repeated ? 'Previously reviewed' : 'When you opened it'}</Th>,
        <Th key="yours">Your draft</Th>,
        <Th key="latest">Latest saved</Th>,
        <Th key="resolution">Resolution</Th>,
      ]}>
        <ReviewRow label="Policy name" field={props.comparison.fields.name} choice={props.choices.name} disabled={props.disabled} onChoose={(choice) => props.onChoose('name', choice)} />
        <ReviewRow label="Selector" field={props.comparison.fields.selector} choice={props.choices.selector} disabled={props.disabled} onChoose={(choice) => props.onChoose('selector', choice)} />
        {props.kind === 'counter' ? <>
          <ReviewRow label="Format priority" field={props.comparison.fields.formatPriority} choice={props.choices.formatPriority} readOnlyLatest={props.formatsReadOnly} disabled={props.disabled} onChoose={(choice) => props.onChoose('formatPriority', choice)} />
          <ReviewRow label="Counters to display" field={props.comparison.fields.countersToDisplay} choice={props.choices.countersToDisplay} disabled={props.disabled} onChoose={(choice) => props.onChoose('countersToDisplay', choice)} />
        </> : <>
          <ReviewRow label="Condition" field={props.comparison.fields.condition} choice={props.choices.condition} disabled={props.disabled} onChoose={(choice) => props.onChoose('condition', choice)} />
          <ReviewRow label="Notification actions" field={props.comparison.fields.actions} choice={props.choices.actions} disabled={props.disabled} onChoose={(choice) => props.onChoose('actions', choice)} />
        </>}
      </DataTable>
      <PrinterPolicyDraftCopy draftText={props.draftText} />
      <div className="border-border flex flex-wrap justify-end gap-2 border-t pt-4">
        <Button type="button" variant="ghost" disabled={props.disabled} onClick={props.onContinue}>Continue editing</Button>
        <Button type="button" variant="outline" disabled={blocked} onClick={props.onEditMerged}>Edit merged policy</Button>
        <Button type="button" disabled={blocked || props.saveDisabled} onClick={props.onSaveResolved}>Save resolved policy</Button>
      </div>
    </section>
  )
}

export function PrinterPolicyDraftCopy({ draftText }: { draftText: string }) {
  const clipboard = useClipboardCopy()
  const id = useId()
  return (
    <div className="flex flex-col items-start gap-2">
      <Button type="button" variant="outline" onClick={() => void clipboard.copy(draftText, { successMessage: 'Your policy changes were copied.' })}>Copy your changes</Button>
      {clipboard.copyFailed && <>
        <p role="alert" className="text-destructive text-xs">Couldn’t copy your changes. Select and copy the draft below.</p>
        <label htmlFor={id} className="text-xs font-medium">Your policy draft</label>
        <textarea id={id} readOnly value={draftText} className={cn(inputClassName, 'h-40 w-full font-mono')} />
      </>}
    </div>
  )
}

const automaticLabels = { unchanged: 'Unchanged', yours: 'Your change', latest: 'Latest change', identical: 'Same change', conflict: 'Choose a version' }

function ReviewRow({ label, field, choice, disabled, readOnlyLatest = false, onChoose }: {
  label: string
  field: PolicyFieldComparison<unknown>
  choice?: PolicyFieldChoice
  disabled: boolean
  readOnlyLatest?: boolean
  onChoose: (choice: PolicyFieldChoice) => void
}) {
  const conflict = field.status === 'conflict'
  const groupName = useId()
  let resolution = field.latest
  if (!readOnlyLatest) {
    if (field.status === 'yours' || field.status === 'identical' || (conflict && choice === 'yours')) resolution = field.yours
    if (conflict && choice === undefined) resolution = undefined
  }
  return (
    <tr className={cn('border-border border-b align-top', conflict && 'bg-warning/10')}>
      <th scope="row" className="px-3 py-2 text-left font-medium whitespace-nowrap">{label}</th>
      {[field.opened, field.yours, field.latest].map((value, index) => <td key={index} className="min-w-40 px-3 py-2"><ReviewValue value={value} /></td>)}
      <td className="min-w-48 px-3 py-2">
        {readOnlyLatest ? <p className="text-muted-foreground mb-2">The latest saved format list is unsupported and read-only here. Its exact values and order will be kept. Your format draft is preserved for review and copying.</p> : <>
          {conflict ? <fieldset className="mb-2 flex flex-wrap gap-2" disabled={disabled}>
            <legend className="sr-only">Resolve {label}</legend>
            <label className="flex items-center gap-1.5"><input type="radio" name={groupName} value="yours" checked={choice === 'yours'} aria-label={`Use yours for ${label}`} onChange={() => onChoose('yours')} className="accent-primary" />Use yours</label>
            <label className="flex items-center gap-1.5"><input type="radio" name={groupName} value="latest" checked={choice === 'latest'} aria-label={`Use latest for ${label}`} onChange={() => onChoose('latest')} className="accent-primary" />Use latest</label>
          </fieldset> : <p className="text-muted-foreground mb-2">{automaticLabels[field.status]}</p>}
        </>}
        {resolution === undefined ? <p className="text-warning-foreground">Choose a version</p> : <ReviewValue value={resolution} />}
      </td>
    </tr>
  )
}

function ReviewValue({ value }: { value: unknown }) {
  return <pre className="whitespace-pre-wrap break-all font-mono">{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
}
